import { Capacitor } from '@capacitor/core'

/** A finger, not a mouse. Read together with the size queries: a touch laptop is not a phone. */
export const COARSE_POINTER_QUERY = '(pointer: coarse)'
/**
 * Android's tablet line (`sw600dp`), applied to the SHORT side of the viewport, so a phone counts in
 * either orientation: held sideways, an iPhone 14 is 844 px wide (a Pixel 7 about 915) but still
 * under 600 px tall, and the largest phones are about 440 px across. Every tablet stays out in
 * both orientations, the 744 px iPad mini included — rotation must not change the answer.
 */
export const PHONE_WIDTH_QUERY = '(max-width: 599px)'
export const PHONE_HEIGHT_QUERY = '(max-height: 599px)'

/** What the predicate reads from the platform, injectable so tests can describe any device. */
export type PhoneProbeT = {
  isNativePlatform: () => boolean
  /** `null` where the environment has no `matchMedia` (some test runners, very old WebViews). */
  matchMedia: ((query: string) => { matches: boolean }) | null
}

export function defaultPhoneProbe(): PhoneProbeT {
  const hasMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  return {
    isNativePlatform: () => Capacitor.isNativePlatform(),
    matchMedia: hasMatchMedia ? query => window.matchMedia(query) : null,
  }
}

/**
 * Whether this device gets the quick-capture screen: a phone, i.e. a screen whose short side is
 * under 600 px (portrait and landscape alike). In a browser it must also be a touch screen; the
 * native app is touch by definition, but a native TABLET is not a phone and stays out, like a
 * tablet browser does. A native WebView without `matchMedia` is let in (it is the app on a device
 * we cannot measure, and the app ships to phones). The route
 * loader asks once at entry and the screen asks again on mount with the same function, because
 * the viewport can change between the two (a resized window) and a desktop-shaped viewport must
 * land on the issue list instead.
 *
 * A probe that throws answers "not a phone": the list is the safe place to land.
 */
function isPhoneSized(matchMedia: NonNullable<PhoneProbeT['matchMedia']>): boolean {
  return matchMedia(PHONE_WIDTH_QUERY).matches || matchMedia(PHONE_HEIGHT_QUERY).matches
}

export function isQuickCapturePhone(probe: PhoneProbeT = defaultPhoneProbe()): boolean {
  try {
    const matchMedia = probe.matchMedia
    if (probe.isNativePlatform()) return matchMedia ? isPhoneSized(matchMedia) : true
    if (!matchMedia) return false
    if (!matchMedia(COARSE_POINTER_QUERY).matches) return false
    return isPhoneSized(matchMedia)
  } catch {
    return false
  }
}
